import { Search, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * iOS-style search field: 16px text (no focus zoom), clear button, search key.
 * `trailing` sits inside the field before the clear button (e.g. a "3 of 12" match count).
 */
export function MobileSearchField({
	value,
	onChange,
	placeholder = 'Search',
	className,
	trailing,
}: {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	className?: string;
	trailing?: ReactNode;
}) {
	return (
		<label
			className={cn(
				'flex h-10 items-center gap-2 rounded-[10px] bg-zinc-200/70 px-3 dark:bg-zinc-800',
				className,
			)}
		>
			<Search className="size-[18px] shrink-0 text-muted-foreground" aria-hidden />
			<input
				type="search"
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder={placeholder}
				aria-label={placeholder}
				enterKeyHint="search"
				autoCapitalize="none"
				autoCorrect="off"
				autoComplete="off"
				spellCheck={false}
				onKeyDown={(e) => {
					if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
				}}
				className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
			/>
			{trailing}
			{value && (
				<button
					type="button"
					aria-label="Clear search"
					onClick={() => onChange('')}
					// 36px to fit the 40px field, with a 44px hit area.
					className="relative -mr-2 inline-flex size-9 shrink-0 items-center justify-center text-muted-foreground before:absolute before:-inset-1 active:opacity-60"
				>
					<XCircle className="size-[18px] fill-muted-foreground/30" />
				</button>
			)}
		</label>
	);
}
